<script lang="ts" setup>
import { computed, ref } from 'vue';
import { formatDateTime } from '@/utils/date-utils';
import { formatFileName } from '@/utils/file-utils';
import { UploadUtils } from '@/utils/upload-utils';
import { UploadType } from '@/gql/graphql';
import { useUploadsStore } from '@/store/uploads';
import {
  BAlert,
  BBadge,
  BButton,
  BButtonGroup,
  BCard,
  BProgress,
  BProgressBar,
  BTable,
  BTooltip,
} from 'bootstrap-vue-next';
import { UPLOAD_STATUS } from '@/utils/upload-utils';
import UploadAbort from '@/components/upload/UploadAbort.vue';
import UploadResume from '@/components/upload/UploadResume.vue';

const uploadsStore = useUploadsStore();

// --- Component Properties ---
const props = defineProps({
  transparentStyle: { type: Boolean, default: false },
  messageNoItemsFound: {
    type: String,
    default: 'There are no active or recently finished uploads.',
  },
});

// --- Component State ---
const noUploads = computed(() => {
  return uploadsStore.uploads.length === 0;
});

const uploadFields = [
  {
    key: 'createdAt',
    label: 'Created At',
    tdClass: 'align-middle',
    formatter: formatDateTime,
    sortable: true,
  },
  {
    key: 'targetFileName',
    label: 'File',
    tdClass: 'align-middle',
    formatter: formatFileName,
    sortable: true,
  },
  {
    key: 'dataRef',
    label: 'Data reference',
    tdClass: 'align-middle',
    sortable: true,
  },
  {
    key: 'cloudProvider',
    label: 'Provider',
    tdClass: 'align-middle text-uppercase',
    sortable: true,
  },
  { key: 'status', label: 'Status', tdClass: 'align-middle', sortable: true },
  { key: 'actions', label: '', tdClass: 'align-middle text-end min-width' },
];

// --- Upload Resume Request ---
function uploadResumeRequested(upload: UploadType) {
  if (!upload.id) {
    console.error('Task resumption requested, but upload ID is missing');
    return;
  }
  selectedUpload.value = upload;
  showUploadResumeModal.value = true;
}

function resumeUploadConfirmed(file: File) {
  // check if the upload object is available
  if (!selectedUpload.value || !selectedUpload.value.id) {
    console.error(
      'Task resumption requested, but upload object or its ID is missing'
    );
    return;
  }
  // resume the upload
  console.debug('Resume upload ', selectedUpload.value.id, 'requested');
  void uploadsStore.resumeUpload(selectedUpload.value, file).catch(() => {
    console.error('Could not resume upload.');
  });
  // close the modal
  showUploadResumeModal.value = false;
  selectedUpload.value = null;
}

function closeUploadResumeModal() {
  showUploadResumeModal.value = false;
  selectedUpload.value = null;
}

// --- Upload Abort Request ---
const selectedUpload = ref<UploadType | null>(null);
const showUploadResumeModal = ref<boolean>(false);
const showConfirmAbortModal = ref<boolean>(false);

function uploadAbortRequested(upload: UploadType) {
  if (!upload.id) {
    console.error('Task cancellation requested, but upload ID is missing');
    return;
  }
  console.debug(
    'Cancel upload ',
    upload.id,
    'requested, waiting for confirmation'
  );
  selectedUpload.value = upload;
  showConfirmAbortModal.value = true;
}

function closeUploadAbortModal() {
  showConfirmAbortModal.value = false;
  selectedUpload.value = null;
}
</script>
<template>
  <div>
    <!-- Error -->
    <b-alert v-model="uploadsStore.error" variant="danger">
      {{ uploadsStore.error?.message }}
    </b-alert>

    <!-- empty list -->
    <b-alert v-model="noUploads" variant="secondary">
      {{ props.messageNoItemsFound }}
    </b-alert>

    <BCard
      v-if="uploadsStore.uploads.length > 0"
      :class="{ 'transparent-card': props.transparentStyle }"
    >
      <b-table
        v-if="!uploadsStore.error && uploadsStore.uploads.length > 0"
        :items="uploadsStore.uploads"
        :fields="uploadFields"
        :striped="!props.transparentStyle"
        thead-tr-class="transparent-table"
        tbody-tr-class="transparent-table"
        :class="[
          'browser-table',
          'm-0',
          {
            'table-transparent': props.transparentStyle,
          },
        ]"
        small
        responsive
        hover
      >
        <template #cell(dataRef)="data">
          <span v-if="data.item.bucket">
            {{ data.item.bucket.friendlyName }}
          </span>
        </template>
        <template #cell(status)="data">
          <b-progress
            :id="`task-progress-${data.item.id}`"
            max="100"
            :animated="UploadUtils.progressActive(data.item.status)"
            :variant="UploadUtils.progressVariant(data.item.status)"
          >
            <!-- Keep the value full-width so status text remains readable at low progress. -->
            <b-progress-bar
              value="100"
              :variant="UploadUtils.progressVariant(data.item.status)"
            >
              <span v-if="data.item.status === UPLOAD_STATUS.RUNNING">
                {{ Math.round(data.item.progress) }}%
              </span>
              <span v-else>{{ data.item.status }}</span>
            </b-progress-bar>
          </b-progress>
          <small
            v-if="uploadsStore.uploadError(data.item.id)"
            class="d-block mt-1 text-danger"
          >
            {{ uploadsStore.uploadError(data.item.id) }}
          </small>
          <BTooltip
            v-if="data.item.status === UPLOAD_STATUS.STALE"
            :target="`task-progress-${data.item.id}`"
          >
            The download appears to have stalled, possibly due to the app being
            closed or reloaded, or due to slow network conditions. This may
            occur if another instance of the app (in a different tab or device)
            is handling the upload. If that’s the case, you can ignore this
            message. Otherwise, click the
            <b-badge variant="light"
              ><font-awesome-icon icon="rotate-right" /> Resume</b-badge
            >
            button to continue the download from its last state.
          </BTooltip>
        </template>
        <template #cell(actions)="data">
          <BButtonGroup>
            <!-- Data Button -->
            <b-button
              size="sm"
              variant="outline-dark"
              :id="`link-bucket-${data.item.id}`"
              :disabled="!data.item.bucket"
              :to="
                data.item.bucket && !data.item.bucket.deletedAt
                  ? {
                      name: 'BucketDetail',
                      params: { id: data.item.bucket.id },
                    }
                  : undefined
              "
            >
              <font-awesome-icon icon="database" />
            </b-button>
            <BTooltip :target="`link-bucket-${data.item.id}`">
              <span v-if="data.item.bucket.deletedAt">
                <font-awesome-icon icon="exclamation-triangle" />
                Data have been deleted
              </span>
              <span v-else-if="data.item.bucket">
                <font-awesome-icon icon="database" class="me-2" />
                <strong>{{ data.item.bucket.friendlyName }}</strong>
              </span>
              <span v-else>
                <font-awesome-icon icon="exclamation-triangle" />
                No Data
              </span>
            </BTooltip>
            <b-button
              :id="`resume-upload-${data.item.id}`"
              :disabled="!UploadUtils.canBeResumed(data.item.status)"
              size="sm"
              variant="outline-dark"
              @click="uploadResumeRequested(data.item)"
            >
              <font-awesome-icon icon="rotate-right" />
            </b-button>
            <BTooltip :target="`resume-upload-${data.item.id}`">
              Resume
            </BTooltip>
            <!-- Abort Button -->
            <b-button
              :disabled="!UploadUtils.canBeAborted(data.item.status)"
              :id="`cancel-upload-${data.item.id}`"
              size="sm"
              variant="outline-danger"
              @click="uploadAbortRequested(data.item)"
            >
              <font-awesome-icon icon="times" />
            </b-button>
            <BTooltip :target="`cancel-upload-${data.item.id}`">
              Abort
            </BTooltip>
          </BButtonGroup>
        </template>
      </b-table>
    </BCard>

    <!-- MODAL: Resume Upload -->
    <UploadResume
      v-if="showUploadResumeModal && selectedUpload"
      :show="showUploadResumeModal"
      :upload="selectedUpload"
      @close="closeUploadResumeModal"
      @success="resumeUploadConfirmed"
    />

    <!-- MODAL: Abort Upload -->
    <UploadAbort
      v-if="showConfirmAbortModal && selectedUpload"
      :show="showConfirmAbortModal"
      :upload="selectedUpload"
      @close="closeUploadAbortModal"
      @close-and-refresh="closeUploadAbortModal"
    />
  </div>
</template>
